/* eslint-disable max-lines-per-function */
import { DownloadIcon } from "@chakra-ui/icons"
import {
  Box,
  Button,
  Flex,
  Text,
  useToast
} from "@chakra-ui/react"
import { useState } from "react"
import bg from "../assets/images/drawingRevitbg.png"
import { breadCrumbFirstLevel, breadCrumbReportsAndResourcesSecondLevel, pageContentWidth } from "../common/commonConstants"
import { buildDocumentUrl } from "../common/documentUrl"
import { BreadCrumb } from "../components/commons/breadcrumb"
import { StaticPageHeader } from "../components/commons/static-page-header"
import { PDFIcon, REVITIcon } from "../components/productInfo/product-info.main"
import { Heading3PT } from "../components/commons/Heading3PT"
import { SubPage, SubPageContent } from "../components/SubPage"
import { Tabs } from "../components/tabs"
import { Colors } from "../theme/colors"
import { useGetDrawingsPageDataQuery } from "../type-and-hooks"

export const DrawingAndRevit = (): JSX.Element => {
  const [clickedTabIndex, setClickedTabIndex] = useState<number>(0)
  const toast = useToast()

  const { data: drawingsPageData } = useGetDrawingsPageDataQuery({ fetchPolicy: "cache-and-network" })

  const categories = drawingsPageData?.getDrawingsPageData ?? []

  const isPdf = clickedTabIndex === 0

  const handlePreview = (url: string, fileName?: string | null): void => {
    window.open(buildDocumentUrl(fileName, url) ?? url, "_blank", "noopener,noreferrer")
  }

  const downloadFile = (url: string, name: string): void => {
    const link = document.createElement("a")
    link.href = url
    link.download = name
    link.target = "_blank"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast({ title: "Download started", status: "success", duration: 3000, isClosable: true })
  }

  return (
    <>
      <SubPage>
        <StaticPageHeader imageOpacity={0.7} title="Product Drawing & Revit Files" bg={bg} />
        <BreadCrumb
          options={{
            ...breadCrumbFirstLevel,
            ...breadCrumbReportsAndResourcesSecondLevel,
            thirdLevel: { title: "Product Drawing & Revit Files" }
          }}
        />
        <SubPageContent innerBoxProps={{ w: ["100%", "100%", "100%", pageContentWidth, "70%", "70%"] }}>
          <Box>
            <Flex align={"center"} flexDir={"column"} mb={[6, 6, "25px"]} w={["100%"]}>
              <Text
                variant={["RBody", "RBody", "Body1Regular"]}
                color={Colors.Secondary.c2}
                textAlign={["justify", "center", "center"]}
                maxW={["580px"]}
                mt="16px"
              >
                The drawings can also be found on the relevant product pages.
              </Text>
            </Flex>
            <Box maxW={["250px", "250px", "100%"]} margin={"auto"}>
              <Tabs
                tabs={["Drawings", "Revit Files"]}
                defaultTabIndex={0}
                tabClickHandler={(_tabType, tabIndex) => {
                  setClickedTabIndex(tabIndex)
                }}
              />
            </Box>
            <Box
              w="100%"
              alignItems={"flex-start"}
              height="auto"
              mt="0"
              display={"grid"}
              gridTemplateColumns={["repeat(1, 1fr)", "repeat(2, 1fr)", "repeat(auto-fit, minmax(200px, 1fr))"]}
              gridColumnGap={"40px"}
            >
              {categories.map(category => {
                const visibleItems = (category?.products ?? []).filter(item => (isPdf ? !!item?.pdf_url : !!item?.revit_url))
                if (!category?.group_name || visibleItems.length === 0) return null
                return (
                  <Box key={category.group_name} mb="32px" display="flex" flexDirection="column">
                    <Box minH="80px" display="flex" alignItems="flex-end" justifyContent="center" mt={["6px", "6px", "0"]}>
                      <Heading3PT text={category.group_name} props={{ textAlign: "center", w: "100%" }} />
                    </Box>
                    <Box borderTop={`1px solid ${Colors.Secondary.c2_300}`} pt={["6px", "6px", "16px"]} mt="8px" display="flex" flexDirection="column" alignItems="center">
                      <Box>
                        {visibleItems.map(item => {
                          const label = item?.generic_code ?? ""
                          const url = (isPdf ? item?.pdf_url : item?.revit_url) as string
                          const fileName = isPdf ? item?.pdf_file_name : item?.revit_file_name
                          const pdfDownloadUrl = item?.pdf_download_url ?? url
                          return (
                            <Flex key={label} mt="10px" align="center" gap="4px">
                              {isPdf && (
                                <Button
                                  variant="linkButton"
                                  p="0"
                                  minW="0"
                                  h="auto"
                                  display="flex"
                                  alignItems="center"
                                  flexShrink={0}
                                  onClick={() => { downloadFile(pdfDownloadUrl, `${label}.pdf`) }}
                                  aria-label={`Download ${label}`}
                                >
                                  <DownloadIcon />
                                </Button>
                              )}
                              <Button
                                ml="3px"
                                minH="22px"
                                fontSize={"15px"}
                                textAlign={"left"}
                                variant={"linkButton"}
                                lineHeight={"18px"}
                                fontWeight={"400"}
                                whiteSpace={"unset"}
                                wordBreak={"break-word"}
                                display="flex"
                                alignItems={"flex-start"}
                                justifyContent={"flex-start"}
                                onClick={isPdf
                                  ? () => { handlePreview(url, fileName) }
                                  : () => { downloadFile(url, `${label}.rfa`) }
                                }
                              >
                                {isPdf ? <PDFIcon /> : <REVITIcon />}
                                <Text variant={["RButton2", "RButton2", "Button2Big"]} as="span" paddingLeft="8px">
                                  {label}
                                </Text>
                              </Button>
                            </Flex>
                          )
                        })}
                      </Box>
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </Box>
        </SubPageContent>
      </SubPage>
    </>
  )
}
